import type {
  FrequencyOfStatisticalImpactChart,
  PickByPrefix,
  StandardDeviationSeverityChart,
} from '~/constants/types/projectDataVisualizations'
import { useChartHasDataCheck } from './useChartHasDataCheck'
import type {
  DataVizTileChartDataOverUnderCountDTO,
  QueriesGetFrequencyAndSeverityChartsResponse,
  QueriesGroups,
} from 'client/sratool'
import type { ChartDataset } from 'chart.js'

export const useFrequencyAndSeverityWithOverUnderDataBarCharts = ({
  selectedGroup,
  chartData,
  laborMarketType,
}: {
  selectedGroup?: QueriesGroups
  chartData?: Ref<QueriesGetFrequencyAndSeverityChartsResponse | undefined>
  laborMarketType?: string
}) => {
  const { dataIsDefined, hasData } = useChartHasDataCheck()

  function transformStandardDeviationSeverityBarChartData<
    T extends StandardDeviationSeverityChart
  >({
    chartData,
    fieldName,
    laborMarketType,
  }: {
    chartData: QueriesGetFrequencyAndSeverityChartsResponse | undefined
    fieldName: 'standardDeviationSeverityByGroupField'
    laborMarketType?: string
  }): T[] {
    if (!chartData) return []
    return [
      // Strata bars (example for up to 5)
      ...[1, 2, 3, 4, 5].reduce<T[]>((acc, index) => {
        const strata: DataVizTileChartDataOverUnderCountDTO =
          chartData[
            `${fieldName}${index}` as keyof PickByPrefix<
              QueriesGetFrequencyAndSeverityChartsResponse,
              typeof fieldName
            >
          ]

        if (
          !strata ||
          strata.groupDisplayName === '' ||
          (selectedGroup &&
            strata.groupFieldGUID !== selectedGroup.groupFieldGUID)
        ) {
          return acc
        }

        const barChartData = strata.chartDataByStrataByGroupFieldValue.reduce(
          (groupData, strataData) => {
            strataData.groupCounts.reduce((groupValues, currentGroup) => {
              const datum: [number, number] = [
                currentGroup.overPercent,
                currentGroup.underPercent,
              ]

              if (!datum.some((value) => dataIsDefined(value))) {
                return groupValues
              }

              groupData[currentGroup.groupFieldValue] =
                groupData[currentGroup.groupFieldValue] || []
              groupData[currentGroup.groupFieldValue].push(datum)

              return groupValues
            }, {})

            return groupData
          },
          {} as Record<string, [number, number][]>
        )

        const datasets = Object.keys(barChartData).map((group) => {
          return {
            label: group,
            data: barChartData[group],
          }
        })

        if (!hasData(datasets)) {
          return acc
        }

        acc.push({
          id: index,
          protectedClass: strata.groupDisplayName,
          laborMarketType,
          chart: {
            data: {
              labels: strata.chartDataByStrataByGroupFieldValue.map(
                (c) => c.strataDisplayName
              ),
              datasets,
            },
          },
          insight: strata.chartInsight,
        } as T)

        return acc
      }, []),
    ]
  }

  function transformFrequencyOfStatisticalImpactBarChartData<
    T extends FrequencyOfStatisticalImpactChart
  >({
    chartData,
    fieldName,
    laborMarketType,
  }: {
    chartData: QueriesGetFrequencyAndSeverityChartsResponse | undefined
    fieldName: 'frequencyOfStatisticalImpactByGroupField'
    laborMarketType?: string
  }): T[] {
    if (!chartData) return []

    // determine number of strata
    const strataInUse = [
      ...(chartData[
        `${fieldName}1` as keyof PickByPrefix<
          QueriesGetFrequencyAndSeverityChartsResponse,
          typeof fieldName
        >
      ]?.chartDataByStrataByGroupFieldValue?.reduce<string[]>((acc, index) => {
        if (!acc.includes(index.strataDisplayName)) {
          acc.push(index.strataDisplayName)
        }

        return acc
      }, []) || null),
    ]

    let frequencyLabelOffset = 0
    const labelSpacing = [] as string[]
    if (strataInUse.length === 1) {
      labelSpacing.push(' ')
      labelSpacing.push(' ')
      labelSpacing.push(' ')
      labelSpacing.push(' ')
      frequencyLabelOffset = 30
    } else if (strataInUse.length === 2) {
      labelSpacing.push(' ')
      labelSpacing.push(' ')
      frequencyLabelOffset = 15
    } else if (strataInUse.length === 3) {
      labelSpacing.push(' ')
      frequencyLabelOffset = 8
    }

    return [
      // Strata bars (example for up to 5)
      ...[1, 2, 3, 4, 5].reduce<T[]>((acc, index) => {
        const strata: DataVizTileChartDataOverUnderCountDTO =
          chartData[
            `${fieldName}${index}` as keyof PickByPrefix<
              QueriesGetFrequencyAndSeverityChartsResponse,
              typeof fieldName
            >
          ]

        if (
          !strata ||
          strata.groupDisplayName === '' ||
          (selectedGroup &&
            strata.groupFieldGUID !== selectedGroup.groupFieldGUID)
        ) {
          return acc
        }

        const barChartData = strata.chartDataByStrataByGroupFieldValue.reduce(
          (groupData, strataData) => {
            strataData.groupCounts.reduce((groupValues, currentGroup) => {
              const datum: [number, number] = [
                currentGroup.overPercent,
                currentGroup.underPercent,
              ]

              if (!datum.some((value) => dataIsDefined(value))) {
                return groupValues
              }

              // ensure group exists first
              groupData[currentGroup.groupFieldValue] =
                groupData[currentGroup.groupFieldValue] || []
              // ensure the stack exists in the group
              groupData[currentGroup.groupFieldValue][currentGroup.stack] =
                groupData[currentGroup.groupFieldValue][currentGroup.stack] ||
                []
              // get the data for this group/stack combo

              // then push it onto the nested array
              groupData[currentGroup.groupFieldValue][currentGroup.stack].push(
                datum
              )

              return groupValues
            }, {})

            return groupData
          },
          {} as Record<string, Record<string, [number, number][]>>
        )

        const barChartDataSeparated =
          strata.chartDataByStrataByGroupFieldValue.reduce(
            (groupData, strataData) => {
              strataData.groupCounts.reduce((groupValues, currentGroup) => {
                const datum: [number, number] = [
                  currentGroup.overPercent,
                  currentGroup.underPercent,
                ]
                if (!dataIsDefined(datum)) {
                  return groupValues
                }
                groupData[currentGroup.groupFieldValue] =
                  groupData[currentGroup.groupFieldValue] || []
                groupData[currentGroup.groupFieldValue].push(datum)
                return groupValues
              }, {})

              return groupData
            },
            {} as Record<string, [number, number][]>
          )

        const axisLabelsWithStacks =
          strata.chartDataByStrataByGroupFieldValue.reduce(
            (axisLabels, strataData) => {
              strataData.groupCounts.reduce((stacks, currentGroup) => {
                axisLabels[strataData.strataDisplayName] =
                  axisLabels[strataData.strataDisplayName] || []

                // only add stacks if they've been customized
                if (currentGroup.stack !== 'default') {
                  const stackName = `${currentGroup.stack}`

                  if (
                    axisLabels[strataData.strataDisplayName].indexOf(
                      stackName
                    ) == -1
                  ) {
                    axisLabels[strataData.strataDisplayName].push(stackName)
                  }
                }
                return stacks
              }, [])

              return axisLabels
            },
            {} as Record<string, string[]>
          )

        const datasets: ChartDataset<'bar'>[] = []
        Object.entries(barChartData).forEach(([label, stacks]) => {
          Object.entries(stacks).forEach(([stackName, dataPoints]) => {
            datasets.push({
              label: label,
              data: dataPoints,
              stack: stackName,
            })
          })
        })

        if (!hasData(datasets)) {
          return acc
        }

        const labels = Object.entries(axisLabelsWithStacks).map(
          ([label, stacks]) => {
            // when adding stacks, need to get some spacing between each one or they all cram together
            // additionally, lineheight spacing out everything, but we only want it between the stacks
            // so here, making the stack name on one line, the "by {strata}" on the second, and two spacers
            // this aligns relatively well with the bars
            if (stacks.length > 0) {
              return [
                ...stacks.flatMap((stack) => [
                  stack,
                  `by ${label}`,
                  ...labelSpacing,
                ]),
              ]
            }

            return label
          }
        )

        acc.push({
          id: index,
          protectedClass: strata.groupDisplayName,
          laborMarketType,
          chart: {
            data: {
              labels: labels,
              datasets: datasets,
            },
            separatedChartData: {
              labels: Object.entries(axisLabelsWithStacks).reduce<string[]>(
                (acc, [label, stacks]) => {
                  acc.push(...stacks.map((stack) => `${stack} by ${label}`))
                  return acc
                },
                []
              ),
              datasets: Object.keys(barChartDataSeparated).map((group) => {
                return {
                  label: group,
                  data: barChartDataSeparated[group],
                }
              }),
            },
          },
          insight: strata.chartInsight,
          frequencyLabelOffset,
        } as T)

        return acc
      }, []),
    ]
  }

  const dataRef = computed(() => chartData?.value)

  const frequencyOfStatisticalImpactCharts = computed(() => {
    return transformFrequencyOfStatisticalImpactBarChartData<FrequencyOfStatisticalImpactChart>(
      {
        chartData: dataRef.value,
        fieldName: 'frequencyOfStatisticalImpactByGroupField',
        laborMarketType,
      }
    )
  })

  const standardDeviationSeverityCharts = computed(() => {
    return transformStandardDeviationSeverityBarChartData<StandardDeviationSeverityChart>(
      {
        chartData: dataRef.value,
        fieldName: 'standardDeviationSeverityByGroupField',
        laborMarketType,
      }
    )
  })

  return {
    frequencyOfStatisticalImpactCharts,
    standardDeviationSeverityCharts,
  }
}
